/** Admin → CIS ledger: every sub-contractor invoice to a LUX company in the CIS tax month (6th → 5th), with the CIS split, exportable as CSV — plus the CIS contractors list. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { addMonths, format, subMonths } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Download, Loader2, Landmark, Inbox, Users, FileText, ExternalLink } from 'lucide-react';
import { CisContractorsPanel } from './cis/CisContractorsPanel';

const gbp = (v) => `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const uk = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');
const STATUS = { issued: 'bg-blue-500/15 text-blue-300 border-blue-500/30', sent: 'bg-blue-500/15 text-blue-300 border-blue-500/30', approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', paid: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40', posted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', submitted: 'bg-gray-500/15 text-gray-300 border-gray-500/30' };
const SOURCE = { v2: { label: 'Field app', cls: 'text-sky-300 border-sky-500/30 bg-sky-500/10' }, legacy: { label: 'Monthly', cls: 'text-gray-300 border-white/15 bg-white/5' }, inbox: { label: 'Cost Inbox', cls: 'text-[#F5A623] border-[#F5A623]/30 bg-[#F5A623]/10' } };

/** CIS tax month: 6th of `month` → 5th of the next month. */
export function monthRange(month) {
  return { start: format(month, 'yyyy-MM-06'), end: format(addMonths(month, 1), 'yyyy-MM-05') };
}
/** The tax month containing `today` (1st–5th belongs to the month that started on the previous 6th). */
export function taxMonthFor(today = new Date()) {
  const base = today.getDate() < 6 ? subMonths(today, 1) : today;
  return new Date(base.getFullYear(), base.getMonth(), 1);
}

export default function CisLedger() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'contractors' ? 'contractors' : 'invoices';
  const setTab = (t) => setParams(t === 'contractors' ? { tab: 'contractors' } : {});
  const [month, setMonth] = useState(() => taxMonthFor());
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const range = useMemo(() => monthRange(month), [month]);

  const load = useCallback(async () => {
    setData(null);
    try { const r = await api.get('/admin/cis-ledger', { params: range }); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load the CIS ledger'); setData({ rows: [], groups: [], totals: {} }); }
  }, [api, range]);
  useEffect(() => { if (tab === 'invoices') load(); }, [load, tab]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/cis-ledger', { params: { ...range, format: 'csv' }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `cis-ledger_${range.start}_to_${range.end}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setExporting(false); }
  };
  const [returning, setReturning] = useState(null);
  const downloadReturn = async (fmt) => {
    setReturning(fmt);
    try {
      const res = await api.get('/admin/cis-ledger/return', { params: { ...range, format: fmt }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `cis-return_${range.start}_to_${range.end}.${fmt}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`CIS return ${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate the CIS return'); }
    finally { setReturning(null); }
  };

  const t = data?.totals || {};
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="cis-ledger-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}><Landmark className="w-7 h-7 text-[#F5A623]" /> CIS ledger</h1>
          <p className="text-sm text-gray-400">Sub-contractor invoices to LUX Developments and LUX Construction by CIS tax month (6th → 5th), with labour, materials and the CIS deducted — ready for the monthly return.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/cost-inbox')} className="border-white/20 text-gray-300 bg-white/5" data-testid="cis-open-inbox"><Inbox className="w-4 h-4 mr-1.5" /> Cost Inbox</Button>
          {tab === 'invoices' && (
            <Button onClick={exportCsv} disabled={exporting || !data?.rows?.length} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="cis-export-csv">{exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> Export CSV</>}</Button>
          )}
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-white/10 bg-[#2A3240] p-1" data-testid="cis-tabs">
        {[['invoices', 'Invoices', FileText], ['contractors', 'CIS contractors', Users]].map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-md text-sm transition-colors ${tab === k ? 'bg-[#F5A623] text-[#1E2530] font-semibold' : 'text-gray-300 hover:bg-white/5'}`} data-testid={`cis-tab-${k}`}>
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {tab === 'contractors' ? <CisContractorsPanel api={api} /> : (
        <>
          <div className="flex items-center justify-between bg-[#2A3240] rounded-lg p-3 border border-white/10 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setMonth(m => subMonths(m, 1))} className="text-gray-400 hover:text-white" data-testid="cis-prev-month"><ChevronLeft className="w-5 h-5" /></Button>
              <div className="text-center min-w-[260px]">
                <div className="text-white font-semibold text-lg" data-testid="cis-month-label">{uk(range.start)} – {uk(range.end)}</div>
                <div className="text-[11px] text-gray-500" data-testid="cis-range-label">CIS tax month · {format(month, 'MMMM yyyy')}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setMonth(m => addMonths(m, 1))} className="text-gray-400 hover:text-white" data-testid="cis-next-month"><ChevronRight className="w-5 h-5" /></Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setMonth(taxMonthFor())} className="text-gray-400 hover:text-white text-xs" data-testid="cis-this-month">This tax month</Button>
            <div className="flex items-center gap-2" data-testid="cis-return-actions">
              <span className="text-[11px] uppercase tracking-wider text-gray-500 mr-1">Monthly return</span>
              <Button size="sm" onClick={() => downloadReturn('pdf')} disabled={!!returning || !data?.rows?.length} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold h-8" data-testid="cis-return-pdf">
                {returning === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1.5" /> CIS return PDF</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadReturn('csv')} disabled={!!returning || !data?.rows?.length} className="border-white/20 text-gray-200 bg-white/5 h-8" data-testid="cis-return-csv">
                {returning === 'csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> CSV</>}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cis-totals">
            {[['Invoices', t.count ?? 0, false], ['Gross', t.gross_total, true], ['Labour (CIS)', t.cis_base, true], ['CIS deducted', t.cis_deduction, true], ['Net payable', t.net_payable, true]].map(([l, v, money]) => (
              <div key={l} className="rounded-lg bg-[#2A3240] border border-white/10 p-3">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">{l}</div>
                <div className={`text-xl font-bold tabular-nums ${l === 'CIS deducted' ? 'text-[#F5A623]' : 'text-white'}`} data-testid={`cis-total-${l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{money ? gbp(v) : v}</div>
              </div>
            ))}
          </div>

          {!data ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div> : data.rows.length === 0 ? (
            <div className="py-16 text-center text-gray-500 rounded-lg bg-[#2A3240] border border-white/10" data-testid="cis-empty">No sub-contractor invoices to LUX in this tax month.</div>
          ) : (
            <div className="space-y-4" data-testid="cis-groups">
              {data.groups.map(g => (
                <div key={g.contractor_id} className="rounded-lg bg-[#2A3240] border border-white/10 overflow-hidden" data-testid={`cis-group-${g.contractor_id}`}>
                  <div className="px-4 py-2.5 flex items-center justify-between border-b border-white/10">
                    <div><span className="text-white font-semibold">{g.contractor_name}</span>{g.utr && <span className="text-xs text-gray-500 ml-2">UTR {g.utr}</span>}</div>
                    <div className="text-xs text-gray-400">{g.totals.count} invoice{g.totals.count === 1 ? '' : 's'} · CIS <span className="text-[#F5A623] font-semibold">{gbp(g.totals.cis_deduction)}</span> · net <span className="text-white font-semibold">{gbp(g.totals.net_payable)}</span></div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500">
                      <tr className="[&>th]:px-4 [&>th]:py-2 [&>th]:text-left [&>th.num]:text-right">
                        <th>Invoice</th><th>To</th><th>Issued</th><th>Period / jobs</th><th className="num">Gross</th><th className="num">Labour</th><th className="num">Materials / fuel <span className="normal-case text-gray-600">exempt</span></th><th className="num">CIS %</th><th className="num">CIS</th><th className="num">Net</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map(r => (
                        <tr key={r.id} className="border-t border-white/5 [&>td]:px-4 [&>td]:py-2 [&>td.num]:text-right [&>td.num]:tabular-nums" data-testid={`cis-row-${r.id}`}>
                          <td className="text-white font-semibold">
                            {r.invoice_number || '—'}
                            <span className={`ml-2 text-[10px] font-normal border rounded px-1.5 py-0.5 whitespace-nowrap ${SOURCE[r.source]?.cls || ''}`} data-testid={`cis-row-source-${r.id}`}>{SOURCE[r.source]?.label || r.source}</span>
                            {r.source === 'inbox' && <button type="button" onClick={() => navigate(`/cost-inbox?open=${r.inbox_item_id}`)} className="ml-1.5 text-gray-500 hover:text-[#F5A623] align-middle" title="Open in Cost Inbox" data-testid={`cis-row-open-${r.id}`}><ExternalLink className="w-3.5 h-3.5 inline" /></button>}
                          </td>
                          <td className={r.company === 'LC' ? 'text-blue-300' : 'text-[#F5A623]'}>{r.company_name}</td>
                          <td className="text-gray-300">{uk(r.issue_date)}</td>
                          <td className="text-gray-400 text-xs">{r.period || '—'}</td>
                          <td className="num text-white">{gbp(r.gross_total)}</td>
                          <td className="num text-gray-300">{gbp(r.cis_base)}</td>
                          <td className="num text-gray-300">{gbp(r.cis_exempt)}</td>
                          <td className="num text-gray-400">{r.cis_rate}%</td>
                          <td className="num text-[#F5A623] font-semibold">{gbp(r.cis_deduction)}</td>
                          <td className="num text-white font-semibold">{gbp(r.net_payable)}</td>
                          <td><Badge variant="outline" className={`text-[10px] capitalize ${STATUS[r.status] || STATUS.submitted}`}>{r.status}{r.paid_at ? ` ${uk(r.paid_at)}` : ''}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
